/** A value split around the part that differs from the value it's compared to. */
export type PoNumberDiffParts = {
  prefix: string
  differing: string
  suffix: string
}

/** Case-insensitive, per character, so offsets always line up with the original strings. */
function sameChar(a: string | undefined, b: string | undefined): boolean {
  return (
    a !== undefined && b !== undefined && a.toUpperCase() === b.toUpperCase()
  )
}

/**
 * Splits `value` into the start and end it shares with `other` and the part in
 * between that differs, so near-identical PO numbers (`PO-10234` vs
 * `PO-10243`) can highlight just the characters that don't match. Compares
 * case-insensitively, since case never makes two PO numbers different.
 */
export function splitPoNumberDiff(
  value: string,
  other: string,
): PoNumberDiffParts {
  const maxShared = Math.min(value.length, other.length)

  let prefixLength = 0
  while (
    prefixLength < maxShared &&
    sameChar(value[prefixLength], other[prefixLength])
  ) {
    prefixLength += 1
  }

  // Stop the suffix before it reaches into the prefix, so a value that is the
  // other with characters added (`1234` vs `12344`) still splits cleanly.
  let suffixLength = 0
  while (
    suffixLength < maxShared - prefixLength &&
    sameChar(
      value[value.length - 1 - suffixLength],
      other[other.length - 1 - suffixLength],
    )
  ) {
    suffixLength += 1
  }

  return {
    prefix: value.slice(0, prefixLength),
    differing: value.slice(prefixLength, value.length - suffixLength),
    suffix: value.slice(value.length - suffixLength),
  }
}
